import qs from 'query-string';
import { defer, throwError } from 'rxjs';
import { ajax } from 'rxjs/ajax';
import { catchError, map } from 'rxjs/operators';

import { APP_BACKUP_PLAYLIST_ORIGIN, APP_CONFIG_BUCKET } from '../constants/envs';
import {
  GRAPHQL_URI,
  NO_PERMISSION_MESSAGE,
  PATH_REGEXP,
  REFRESH_SIGNED_URLS_ENDPOINT_URL,
  REJECT_REQUEST_BY_USER_CODE,
  REQUEST_WATCH_CONFIG_URI,
  TOKEN_EXPIRED_MESSAGE,
  VM_API_TM_V3_PROXY_URI,
} from '../constants/request';
import { TYPE_ERROR } from '../constants/snackbar';
import { DEVICE_DESKTOP, DEVICE_MOBILE } from '@/modules/common/constants/device';

import { RawWatchType } from '@/modules/common/types';

import { notifyAction } from '../redux/slices/snackbar';
import { isMobileUserAgent } from '@/modules/common/helpers/userAgent';
import * as commonSelectors from '@/modules/common/redux/selectors/common';

import { getWatchModel } from './config';
import { getChannelArray, getFolderName, getOverrideConfig, getToken, isInternalType } from './embedCodeParams';
import { videoManagerFeatureFlags } from './featureFlags';
import logger from './logger';

/**
 * @param publisherSlug {string}
 * @param playerName {string}
 * @param channelId {number}
 * @returns {`${string}/widget/${string}/${string}/pl_${string}.json`}
 */
export const getBackupPlaylistUrl = (publisherSlug: string, playerName: string, channelId: string) =>
  `${APP_BACKUP_PLAYLIST_ORIGIN}/widget/${publisherSlug}/${playerName}/pl_${channelId}.json`;

let reduxStore: StoreType;
export const setStoreForRequestFn = (store$: StoreType) => {
  reduxStore = store$;
};

type ErrorObjectType = {
  message?: string;
  response?: {
    error?: string;
    message?: string;
    details?: {
      source?: string;
    };
    errors?: {
      message?: string;
    }[];
  };
};

const parseErrorMessage = (errorObject: ErrorObjectType) => {
  if (errorObject.response) {
    const dataErrorMessage = errorObject.response.errors?.[0]?.message;

    if (dataErrorMessage) {
      return [dataErrorMessage];
    }

    return Array.isArray(errorObject.response.details)
      ? errorObject.response.details.map((detail) => {
          const mainMessagePart = errorObject.response!.message ?? errorObject.message;
          const source = detail.source ? `${detail.source}: ` : '';

          return `${mainMessagePart} ${source}${detail.message}`;
        })
      : [errorObject.response.message ?? errorObject.response.error ?? errorObject.message];
  }

  return [errorObject.message];
};

const tokenExpiredReg = new RegExp(`${TOKEN_EXPIRED_MESSAGE}`, 'i');
const noPermissionsReg = new RegExp(`${NO_PERMISSION_MESSAGE}`, 'i');

type gqlErrorsType = { message: string; code: number }[];

const processResponse = (errors: gqlErrorsType) => {
  if (errors.length) {
    let notifyMessage = null;
    const tokenWasExpired = errors.some(({ message }) => tokenExpiredReg.test(message));
    const noPermission = errors.some(({ message }) => noPermissionsReg.test(message));

    if (tokenWasExpired) {
      notifyMessage = TOKEN_EXPIRED_MESSAGE;
      try {
        window.top?.logOut?.();
      } catch (e) {
        logger.error(TOKEN_EXPIRED_MESSAGE, e);
      }
    } else if (noPermission) {
      notifyMessage = NO_PERMISSION_MESSAGE;
    } else if (errors[0].code !== REJECT_REQUEST_BY_USER_CODE) {
      notifyMessage = parseErrorMessage(errors[0]).shift();
    }

    if (notifyMessage) {
      reduxStore.dispatch(
        notifyAction({
          key: [TOKEN_EXPIRED_MESSAGE, NO_PERMISSION_MESSAGE].includes(notifyMessage) ? notifyMessage : undefined,
          variant: TYPE_ERROR,
          message: notifyMessage,
        }),
      );
    }
  }
};

type gqlRequestType = { query: string; variables?: Record<string, unknown> };

export const gqlFetch = (body: gqlRequestType) => {
  const headers = new Headers({
    Accept: 'application/json',
    Authorization: getToken(),
    'Content-Type': 'application/json',
  });

  if (videoManagerFeatureFlags.logs) {
    headers.set('aclog', 'true');
    // eslint-disable-next-line no-console
    console.log('Request log: ', body);
  }

  return fetch(GRAPHQL_URI, {
    method: 'POST',
    mode: 'cors',
    headers,
    credentials: 'include',
    body: JSON.stringify({
      query: body.query,
      variables: body.variables ?? {},
    }),
  })
    .then(async (response) => {
      const { errors = [], ...restData } = (await response.json()) ?? {};

      const errorSummary = Object.entries(restData).reduce(
        (acc, [key, value]) => (key === 'errors' ? acc.concat(...(value as string[])) : acc),
        errors,
      );

      processResponse(errorSummary);

      return {
        ...restData,
        errors: errorSummary,
      };
    })
    .catch((error) => {
      const errors = [error];

      processResponse(errors);

      return { data: null, errors };
    });
};

export const gqlRequest = (body: gqlRequestType) => defer(() => gqlFetch(body));

export const getSourceUrl = () => {
  const { hostname, pathname } = window.location;

  return `${hostname}${pathname.replace(/\//, '') ? pathname : ''}`;
};

const sessionStartDate = Date.now();

const getV3VMProxyUrl = (() => {
  const memo = {
    [REFRESH_SIGNED_URLS_ENDPOINT_URL]: REFRESH_SIGNED_URLS_ENDPOINT_URL,
  };

  return (url: string) => {
    if (!memo[url]) {
      const path = url.split(PATH_REGEXP).pop();

      memo[url] = `${VM_API_TM_V3_PROXY_URI}/${path}`;
    }

    return memo[url];
  };
})();

export const includeFeatures = (state: RootState) => ({
  includeChapters: commonSelectors.chaptersSelector(state),
  includeSlides: commonSelectors.slidesSelector(state),
  includeHighlights: commonSelectors.highlightsSelector(state),
});

export const tmPostRequest = (props: { url: string; timeout?: number; body: Record<string, unknown> }) =>
  ajax({
    url: !isInternalType ? props.url : getV3VMProxyUrl(props.url),
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    crossDomain: true,
    timeout: props.timeout ?? 0,
    body: JSON.stringify({
      session: `${getFolderName()}--${sessionStartDate}`,
      sourceUrl: getSourceUrl(),
      dev: isMobileUserAgent ? DEVICE_MOBILE : DEVICE_DESKTOP,
      ...props.body,
    }),
  }).pipe(
    map((ajaxResponse) => {
      const response$ = ajaxResponse as { response?: { errors?: unknown } };

      if (isInternalType) {
        processResponse((response$?.response?.errors as gqlErrorsType) ?? []);
      }

      return ajaxResponse;
    }),
    catchError((ajaxError) => {
      if (isInternalType) {
        processResponse(ajaxError?.response?.errors ?? []);
      }

      return throwError(() => ajaxError);
    }),
  );

const internalFetchConfig = (path: string) =>
  fetch(`${REQUEST_WATCH_CONFIG_URI}?${qs.stringify({ path })}`, {
    method: 'GET',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
      Authorization: getToken(),
    },
    credentials: 'include',
  })
    .then(async (response) => {
      let data = {
        errors: [],
      };
      let errors;

      try {
        data = await response.json();

        errors = (data?.errors as gqlErrorsType) ?? [];
      } catch {
        errors = [
          {
            message: TOKEN_EXPIRED_MESSAGE,
          },
        ];
      }

      if (errors.length) {
        throw errors;
      }

      return data;
    })
    .catch((errors) => {
      processResponse(errors);

      throw errors[0];
    });

const externalFetchConfig = (path: string) =>
  fetch(`${path}/config.json`)
    .then((response) => response.json())
    .catch((errors) => {
      processResponse(errors);

      throw errors[0];
    });

export const fetchConfig = async () => {
  const channelsToView = getChannelArray();
  const overrideConfig = getOverrideConfig();
  const configPath = `${APP_CONFIG_BUCKET}/${getFolderName()}`;
  const config = (await (isInternalType
    ? internalFetchConfig(configPath)
    : externalFetchConfig(configPath))) as Partial<RawWatchType>;

  logger.log('RAW config', config);

  return getWatchModel({
    ...config,
    ...overrideConfig,
    channels: (config.channels || [])
      .filter((channel) => (Array.isArray(channelsToView) ? channelsToView.includes(channel.id) : true))
      .map((sourceChannel) => ({
        ...sourceChannel,
        ...((overrideConfig.channels || []).find((chl$) => chl$.id === sourceChannel.id) || {}),
      })),
  });
};

export const safetyJsonParse = (str: string, defaultValue = {}) => {
  try {
    return JSON.parse(str);
  } catch {
    return defaultValue;
  }
};
